import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";

/**
 * Same shape usePolling.ts's PollState<T> already returns — kept identical
 * on purpose so converting a poll call site to react-query (lib/queryClient.ts)
 * is a drop-in swap for every consumer that just reads `.data`/`.error`.
 */
export interface GlobalQueryState<T> {
  data: T | null;
  error: string | null;
  /**
   * True ONLY when there is genuinely no data at all yet — a truly
   * first-ever visit with an empty persisted cache. This is the one
   * legitimate "loading" state per the owner's rule: once ANY data exists
   * (including data restored from IndexedDB before this fetch even
   * started), `loading` is false and callers must render that data, not a
   * spinner.
   */
  loading: boolean;
  /** True once this query has EVER had data — including data restored from
   * the persisted cache, and INCLUDING after a later background refetch
   * fails. Deliberately derived from data presence (`query.data !==
   * undefined`), NOT `query.status`: react-query's own reducer sets
   * `status: 'error'` on ANY failed fetch, even a background refetch that
   * happens while perfectly good data (live or persisted) is already
   * sitting in the cache (see @tanstack/query-core's query.ts `case
   * 'error'` — it flags the query invalidated but does not clear `data`,
   * yet still flips `status` away from 'success'). Slice-1 used
   * `status === "success"` here, which was the actual cause of a real bug
   * (2026-09-30): Nav.tsx guards on `!featuresEverLoaded && featuresError`,
   * so a single failed background refetch of /api/features (e.g. the
   * backend blipping, or /api/* blocked while a persisted copy exists)
   * flipped `everSucceeded` back to false and showed "Nav unavailable —
   * backend unreachable" even though the persisted nav data was sitting
   * right there in the cache. Data presence doesn't have this problem: it
   * only ever goes from false to true, never back. */
  everSucceeded: boolean;
  /** Wall-clock time (ms since epoch) this query's `data` was last
   * successfully updated — `0` if it has never succeeded. Restoring from
   * the persisted cache counts (persistQueryClient restores
   * `dataUpdatedAt` verbatim), so this reflects "how old is what's on
   * screen right now," including across a reload. Used by the stale-data
   * indicator (see lib/definitions.ts's STALE_DATA_NOTE / AppData.tsx). */
  dataUpdatedAt: number;
}

/**
 * Thin useQuery wrapper for AppData's global, persisted polls (slice 1:
 * features/health/stats/scopedStats/observers/regions; slice 2 adds
 * displaySettings/simulatedNodes — see AppData.tsx). Every query that goes
 * through this is tagged `meta.persist = true` by default, which is the
 * ONLY thing lib/queryClient.ts's shouldDehydrateQuery checks to decide
 * what gets written to IndexedDB — auth/session data must never be fetched
 * through this hook. Pass `persist: false` for a global poll that
 * shouldn't be written to disk (none needed it as of slice 2, but the
 * option exists rather than forcing every future call site to persist).
 */
export function useGlobalQuery<T>(
  queryKey: readonly unknown[],
  path: string,
  refetchIntervalMs: number,
  enabled = true,
  persist = true,
): GlobalQueryState<T> {
  const query = useQuery<T>({
    queryKey,
    queryFn: ({ signal }) => apiGet<T>(path, signal),
    refetchInterval: enabled ? refetchIntervalMs : false,
    enabled,
    // Every global query is safe to show immediately on a key change
    // (e.g. scopedStats' protocol/provenance-keyed path) using whatever
    // the previous key's data was, rather than a spinner, but real key
    // changes here are rare (filter switches) and StatusStrip already
    // guards against showing a mismatched scope's numbers itself
    // (backendScoped check) — no placeholderData needed on top of that.
    meta: { persist },
  });

  return {
    data: query.data ?? null,
    error: query.error ? (query.error instanceof Error ? query.error.message : String(query.error)) : null,
    loading: query.data === undefined && query.fetchStatus === "fetching",
    everSucceeded: query.data !== undefined,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
